import React from 'react'
const { useState, useEffect } = React;
import { getAuthHeaders } from '../globals.js';

// Titles and current versions of the library entries, for the Diagnostic panel
// (Oike administrator only). Read once per page load.
let titlesPromise = null;
export function useMethodTitles() {
  const [titles, setTitles] = useState(null);
  useEffect(() => {
    titlesPromise = titlesPromise || fetch('/api/method-library', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ action: 'list' }) })
      .then(r => (r.ok ? r.json() : { entries: [] }))
      .then(d => Object.fromEntries((d.entries || []).map(e => [e.id, { title: e.title, version: e.version }])))
      .catch(() => { titlesPromise = null; return {}; });
    let alive = true;
    titlesPromise.then(t => alive && setTitles(t));
    return () => { alive = false; };
  }, []);
  return titles;
}

const PLACE_LABELS = { diagnostic: 'Diagnostic', messages: 'Mensajes', proposals: 'Propuestas' };

/** Why an output used no entry, from the counts the server stored (never text). */
function whyNone(method) {
  if (typeof method.active !== 'number') return '';
  const place = PLACE_LABELS[method.appliesTo] || method.appliesTo || 'este paso';
  if (method.active === 0) return 'no había ninguna entrada activa';
  if (method.applicable === 0) return `había ${method.active} ${method.active === 1 ? 'entrada activa' : 'entradas activas'}, pero ninguna con "${place}" en Dónde aplica`;
  if (method.omitted) return 'ninguna entró por el tope de largo';
  return '';
}

/**
 * "Usó N entradas de la biblioteca", under an AI output. Always shown when the
 * output went through the library, also with 0 ("No usó entradas…", with the
 * reason), so a 0 is never confused with an error. `method` is what the server
 * stored or answered: { used: [{ id, version }], omitted, appliesTo?, active?,
 * applicable?, error?, errorDetail?, entries?: [{ id, version, title }] }.
 * `titles` (Diagnostic panel): { id: { title, version } } from the library.
 * `expected`: the output should have a record (P3 / P5); without one it says so.
 * Never the text of the entries: only titles and versions.
 */
export default function MethodUsedNote({ method, titles = null, expected = false, style = {} }) {
  const [open, setOpen] = useState(false);
  const base = { fontSize: 11, color: 'var(--globant-muted)', marginTop: 6, ...style };
  if (!method) {
    return expected ? <div style={base}>📚 Sin registro de la biblioteca: esta versión se generó antes de la Biblioteca del método.</div> : null;
  }
  const used = Array.isArray(method.used) ? method.used : [];
  if (method.error) {
    return (
      <div style={{ ...base, color: '#9A6212' }}>
        📚 No se pudo leer la biblioteca: esta salida se generó sin ella.
        {method.errorDetail && <span style={{ fontFamily: 'monospace', marginLeft: 4 }} title="Detalle de Airtable (solo tipo y campo, sin valores)">{method.errorDetail}</span>}
      </div>
    );
  }
  if (!used.length) {
    const why = whyNone(method);
    return <div style={base}>📚 No usó entradas de la biblioteca{why ? `: ${why}` : ''}.</div>;
  }
  const titleOf = (u) => method.entries?.find(e => e.id === u.id)?.title || titles?.[u.id]?.title || 'Entrada sin título';
  return (
    <div style={base}>
      <button type="button" onClick={() => setOpen(v => !v)}
        style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', fontSize: 11, textDecoration: 'underline dotted' }}>
        📚 Usó {used.length} {used.length === 1 ? 'entrada' : 'entradas'} de la biblioteca{method.omitted ? ` (${method.omitted} quedaron afuera por el tope de largo)` : ''}
      </button>
      {open && (
        <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
          {used.map(u => {
            const now = titles?.[u.id]?.version;
            return (
              <li key={u.id}>
                {titleOf(u)} · v{u.version}{now && now !== u.version ? ` (hoy v${now})` : ''}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * The detail of a P3 / P5 output: a visible box with the indicator and, to
 * check it, the raw Inputs.methodLibrary record of THIS version (ids, versions
 * and counts, never text). An edited version without its own record shows the
 * one of the version it was edited from, and says so. Oike administrator only.
 */
export function MethodLibraryRecord({ output, titles }) {
  const [show, setShow] = useState(false);
  const record = output?.methodLibrary ?? output?.refs?.methodLibrary ?? null;
  const from = output?.methodLibraryFrom || null;
  return (
    <div style={{ border: '1px solid var(--globant-border)', borderRadius: 8, padding: '8px 12px', margin: '10px 0', fontSize: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
        Biblioteca del método · registro de la v{output?.version}{from ? ` (heredado de la v${from.version}, de la que se editó)` : ''}
      </div>
      <MethodUsedNote method={record} titles={titles} expected style={{ fontSize: 13, color: 'var(--globant-text)' }} />
      <button type="button" onClick={() => setShow(v => !v)}
        style={{ marginTop: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--globant-muted)', fontSize: 11, textDecoration: 'underline dotted' }}>
        {show ? '▾ Ocultar' : '▸ Ver'} Inputs.methodLibrary
      </button>
      {show && (
        <pre style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '6px 0 0', color: 'var(--globant-text)', background: 'rgba(30,42,44,0.03)', padding: 8, borderRadius: 6 }}>
          {record ? JSON.stringify(record, null, 2) : `La v${output?.version} no tiene Inputs.methodLibrary.`}
        </pre>
      )}
    </div>
  );
}
